import {inject, observer} from "mobx-react";
import * as React from 'react';
import LazyLoad from "react-lazyload";
import {Link} from "react-router-dom";
import cartIconPng from '../../assets/icons/Cart.png';
import cartIconWebp from '../../assets/icons/Cart.webp';
import navbarBrandPng from '../../assets/imgs/NavbarBrand.png';
import navbarBrandWebp from '../../assets/imgs/NavbarBrand.webp';
import {SysOptionEnum} from "../../models/sys/sys-option";
import {LanguageEnum} from "../../models/translation";
import CartStore from "../../stores/cart-store";
import Base, {IBaseProps} from "../base";
import './nav-menu.css';

interface INavMenuProps extends IBaseProps {
    cartStore?: CartStore
}

@inject('rootStore', 'cartStore')
@observer
export default class NavMenu extends Base<INavMenuProps> {
    private static adjustNavbarOnClick() {
        const navbarExpanded = document.getElementById('navbarToggler').attributes.getNamedItem('aria-expanded').value;

        if (navbarExpanded === 'true') {
            NavMenu.adjustNavbar();
        } else {
            NavMenu.resetNavbarAdjustments();
        }
    }

    private static resetNavbarAdjustments() {
        const navbar = document.getElementById('navbar');

        if (!navbar.classList.contains('bg-transparent')) {
            navbar.classList.add('bg-transparent');
            navbar.classList.remove('navbar-black');
        }

        document.getElementById('custom-nav-toggler-icon').classList.toggle('open');
    }

    private static adjustNavbar() {
        const navbar = document.getElementById('navbar');

        if (!navbar.classList.contains('navbar-black')) {
            navbar.classList.add('navbar-black');
            navbar.classList.remove('bg-transparent');
        }

        document.getElementById('custom-nav-toggler-icon').classList.toggle('open');
    }

    private static closeNavbar() {
        NavMenu.resetNavbarAdjustments();
        document.getElementById('navbarSupportedContent').classList.toggle('show');
    };

    public render(): React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | React.ReactNodeArray | React.ReactPortal | boolean | null | undefined {
        const currentLanguage = this.rootStore.translationStore.language;
        const ordersAllowed = this.isOrdersAllowed();
        const blogOption = this.getOption(SysOptionEnum.BlogAllowed);
        const props = this.props;
        const cart = props.cartStore.cart;
        const cartRows = cart && cart.rows;

        return (
            <nav id="navbar" className="navbar fixed-top navbar-expand-xs">
                <div className="container-fluid navbar-margin">
                    <div className="col-xs-6 text-left navbar-left">
                        <Link className="navbar-brand" to="/">
                            <LazyLoad>
                                <picture>
                                    <source srcSet={navbarBrandWebp} type="image/webp"/>
                                    <source srcSet={navbarBrandPng} type="image/png"/>
                                    <img className="img-fluid" src={navbarBrandPng} alt="Navbar brand"/>
                                </picture>
                            </LazyLoad>
                        </Link>
                    </div>
                    <div className="col-xs-6 text-right">
                        <div className="row">
                            {ordersAllowed && cartRows && <div>
                                <Link className="navbar-brand custom-shopping-cart mr-4" to="/cart">
                                    <LazyLoad>
                                        <picture>
                                            <source srcSet={cartIconWebp} type="image/webp"/>
                                            <source srcSet={cartIconPng} type="image/png"/>
                                            <img className="img-fluid" src={cartIconPng} alt="Cart"/>
                                        </picture>
                                    </LazyLoad>
                                    <span className="fa-stack cart-counter" data-count={cartRows.length}/>
                                </Link>
                            </div>}
                            <div className="btn-group btn-group-custom">
                                <button id="selected-language" className="btn btn-circle text-uppercase"
                                        data-toggle="dropdown">{props.rootStore.translationStore.language}</button>
                                <ul className="dropdown-menu dropdown-menu-right dropdown-menu-custom">
                                    {currentLanguage !== LanguageEnum.en &&
                                    <li className="dropdown-item dropdown-item-custom p-0">
                                        <button className="btn btn-circle"
                                                onClick={this.changeLanguage(LanguageEnum.en)}>EN
                                        </button>
                                    </li>}
                                    {currentLanguage !== LanguageEnum.et &&
                                    <li className="dropdown-item dropdown-item-custom p-0">
                                        <button className="btn btn-circle"
                                                onClick={this.changeLanguage(LanguageEnum.et)}>ET
                                        </button>
                                    </li>}
                                </ul>
                            </div>
                            <button id="navbarToggler"
                                    className="navbar-toggler navbar-toggler-white border-transparent"
                                    type="button"
                                    data-toggle="collapse"
                                    data-target="#navbarSupportedContent" aria-controls="navbarSupportedContent"
                                    aria-expanded="false" aria-label="Toggle navigation"
                                    onClick={NavMenu.adjustNavbarOnClick}>
                                <div id="custom-nav-toggler-icon">
                                    <span/>
                                    <span/>
                                    <span/>
                                    <span/>
                                    <span/>
                                    <span/>
                                </div>
                            </button>
                        </div>
                    </div>
                    <div className="collapse navbar-collapse" id="navbarSupportedContent">
                        <ul className="navbar-nav mr-auto">
                            <li className="nav-item">
                                <Link className="nav-link text-nav" to="/products"
                                      onClick={NavMenu.closeNavbar}>{this.translateProductsLink()}</Link>
                            </li>
                            {blogOption === "true" && <li className="nav-item">
                                <Link className="nav-link text-nav" to="/blog"
                                      onClick={NavMenu.closeNavbar}>{this.translate("nav-menu.blog", "Blog")}</Link>
                            </li>}
                            <li className="nav-item">
                                <Link className="nav-link text-nav" to="/about"
                                      onClick={NavMenu.closeNavbar}>{this.translate("nav-menu.about", "About")}</Link>
                            </li>
                            {ordersAllowed && <li className="nav-item">
                                <Link className="nav-link text-nav" to="/cart"
                                      onClick={NavMenu.closeNavbar}>{this.translate("nav-menu.cart", "Cart")}</Link>
                            </li>}
                            <li className="nav-item">
                                <Link className="nav-link text-nav" to="/contact"
                                      onClick={NavMenu.closeNavbar}>{this.translate("nav-menu.contact", "Contact")}</Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>
        );
    }

    private translateProductsLink(): string {
        const ordersAllowed = this.isOrdersAllowed();

        if (ordersAllowed) {
            return this.translate("nav-menu.shop", "Shop");
        }

        return this.translate("nav-menu.products", "Products");
    }

    private changeLanguage = (language: LanguageEnum) => () => {
        document.getElementById('selected-language').textContent = language;
        this.props.rootStore.translationStore.changeLanguage(language);
    };
}



// WEBPACK FOOTER //
// ./src/components/nav-menu/nav-menu.tsx